import React from 'react'
import {
  List,
  useTable,
  EditButton,
  ShowButton,
  DeleteButton,
  DateField,
} from '@refinedev/antd'
import { useNavigation, useList } from '@refinedev/core'
import { Space, Table, Tag } from 'antd'
import type { Tables } from '@muaa/shared'
import { renderStatus, renderClass, renderAvatarName } from '../../theme/columns'
import { ListPage, TableSurface, type Stat } from '../../theme/listkit'
import { UsersRound, Activity, ShieldCheck, CalendarCheck } from '../../components/icons'

type Profile = Tables<'profiles'>

export function ProfileList() {
  const { show } = useNavigation()
  const { tableProps } = useTable<Profile>({
    resource: 'profiles',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })

  // "Last active" from product analytics (admin-gated view), mapped by user_id.
  // Scoped to ONLY the user_ids on the current page so we never load the whole
  // analytics table just to fill one column.
  const pageUserIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          ((tableProps.dataSource as Profile[] | undefined) ?? [])
            .map((r) => r.user_id)
            .filter(Boolean) as string[],
        ),
      ),
    [tableProps.dataSource],
  )
  const { result: usageRes } = useList<{ user_id: string; last_seen: string | null }>({
    resource: 'analytics_user_stats',
    pagination: { mode: 'off' },
    filters: pageUserIds.length
      ? [{ field: 'user_id', operator: 'in', value: pageUserIds }]
      : [],
    queryOptions: { enabled: pageUserIds.length > 0 },
  })
  const lastActive = React.useMemo(() => {
    const m = new Map<string, string>()
    for (const u of usageRes?.data ?? []) if (u.user_id && u.last_seen) m.set(u.user_id, u.last_seen)
    return m
  }, [usageRes])

  // Summary deck — BOUNDED count queries. PostgREST returns `total` (the row
  // count) without shipping the rows, so each number is a single pageSize:1 read
  // instead of an unbounded fetch + client-side reduce.
  const weekAgoIso = React.useMemo(
    () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    [],
  )
  const { result: totalRes } = useList<Profile>({
    resource: 'profiles',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const { result: newWeekRes } = useList<Profile>({
    resource: 'profiles',
    pagination: { pageSize: 1 },
    filters: [{ field: 'created_at', operator: 'gte', value: weekAgoIso }],
    liveMode: 'off',
  })
  const { result: adminsRes } = useList<Profile>({
    resource: 'profiles',
    pagination: { pageSize: 1 },
    filters: [{ field: 'role', operator: 'eq', value: 'admin' }],
    liveMode: 'off',
  })
  const { result: withClassRes } = useList<Profile>({
    resource: 'profiles',
    pagination: { pageSize: 1 },
    filters: [{ field: 'class_year', operator: 'nnull', value: null }],
    liveMode: 'off',
  })

  const total = totalRes?.total ?? 0
  const newThisWeek = newWeekRes?.total ?? 0
  const admins = adminsRes?.total ?? 0
  const withClass = withClassRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Members', value: total.toLocaleString(), icon: UsersRound, tone: 'jaguar' },
    { label: 'New This Week', value: newThisWeek.toLocaleString(), hint: 'last 7 days', icon: Activity, tone: 'gold' },
    { label: 'Admins', value: admins.toLocaleString(), icon: ShieldCheck, tone: 'green' },
    {
      label: 'With Class Year',
      value: withClass.toLocaleString(),
      hint: total ? `${Math.round((withClass / total) * 100)}%` : undefined,
      icon: CalendarCheck,
      tone: 'gold',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Members · Alumni',
        description:
          'The full alumni directory — every graduate and friend of Miami Union in the network. Browse profiles, honor-year classes, and recent activity to keep the community connected.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table
            {...tableProps}
            rowKey="id"
            scroll={{ x: true }}
            onRow={(record) => ({
              onClick: () => show('profiles', record.id),
              style: { cursor: 'pointer' },
            })}
          >
            <Table.Column<Profile> dataIndex="name" title="Name" render={renderAvatarName} />
            {/* Which members still have no name they gave us. 'derived' means the
                display name is the email local part (e.g. an Apple relay id like
                "5j44ftwstb") and the app is showing them the blocking name gate;
                'provider' means it came from Apple/Google and is not yet
                confirmed. Surfaced so staff can see who is stuck rather than
                discovering it from a support message. */}
            <Table.Column<Profile>
              dataIndex="name_source"
              title="Name from"
              width={120}
              render={(v: string | null) =>
                v === 'member' ? (
                  <Tag color="green">member</Tag>
                ) : v === 'provider' ? (
                  <Tag color="gold">provider</Tag>
                ) : (
                  <Tag color="red">needs name</Tag>
                )
              }
            />
            <Table.Column<Profile> dataIndex="email" title="Email" />
            <Table.Column<Profile> dataIndex="class_year" title="Class Year" render={renderClass} />
            <Table.Column<Profile> dataIndex="city" title="City" />
            <Table.Column<Profile> dataIndex="role" title="Role" render={renderStatus} />
            <Table.Column<Profile>
              dataIndex="created_at"
              title="Joined"
              render={(value: string) => <DateField value={value} />}
            />
            <Table.Column<Profile>
              title="Last Active"
              dataIndex="user_id"
              render={(_: unknown, record: Profile) => {
                const ts = record.user_id ? lastActive.get(record.user_id) : undefined
                return ts ? <DateField value={ts} /> : <span style={{ color: '#9aa3b2' }}>—</span>
              }}
            />
            <Table.Column<Profile>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Profile) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
